import React, { useState } from 'react';
import { Form, Input, Button, message, Select, InputNumber } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { ContractDataRow } from './ContractDataRow';
import { WeightDataRow } from './WeightDataRow';
import { SectionHeader } from './SectionHeader';
import { SectionDivider } from './SectionDivider';
import { GridLayout } from './GridLayout';
import { SharedContainerIndicator } from './SharedContainerIndicator';
import { spacing } from '../design-system/tokens';
import { Container } from './types';
import api from '../../services/api';

const { Option } = Select;

interface ContainerCardProps {
  container: Container;
  containerIndex: number;
}

const ContainerCard: React.FC<ContainerCardProps> = ({ container, containerIndex }) => {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleEdit = () => {
    // Initialize form with current values
    // Note: grossWeight and netWeight are already numbers from the API
    form.setFieldsValue({
      containerNumber: container.details?.containerNumber || '',
      sealNumber: container.details?.sealNumber || '',
      equipmentType: transformEquipmentTypeToEnum(container.details?.equipment),
      grossWeight: container.details?.grossWeight ? Number(container.details.grossWeight) : 0,
      netWeight: container.details?.netWeight ? Number(container.details.netWeight) : 0,
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();
      
      const apiData: any = {};
      if (values.containerNumber !== undefined) apiData.container_number = values.containerNumber;
      if (values.sealNumber !== undefined) apiData.seal_number = values.sealNumber;
      if (values.equipmentType !== undefined) apiData.equipment_type = values.equipmentType;
      if (values.grossWeight !== undefined) apiData.gross_weight = values.grossWeight;
      if (values.netWeight !== undefined) apiData.net_weight = values.netWeight;
      
      const currentContainerNumber = container.details?.containerNumber || `${containerIndex + 1}`;
      await api.patch(`/containers/${currentContainerNumber}`, apiData);
      
      message.success('Container updated successfully');
      setIsEditing(false);
      window.location.reload();
    } catch (error: any) {
      message.error(error.message || 'Failed to update container');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIsEditing(false);
  };

  const transformEquipmentTypeToEnum = (equipment?: string): string | undefined => {
    if (!equipment) return undefined;
    const map: { [key: string]: string } = {
      "20'": 'TWENTY_FT',
      "20ft": 'TWENTY_FT',
      "40'": 'FORTY_FT',
      "40ft": 'FORTY_FT',
      "40' HC": 'FORTY_HC',
      "20' Reefer": 'TWENTY_REEFER',
      "40' Reefer": 'FORTY_REEFER',
      'Air Freight': 'AIR_FREIGHT',
      'LCL': 'LCL',
      'Truck': 'TRUCK',
    };
    return map[equipment];
  };

  const displayContainerNumber = container?.containerNumber && container.containerNumber !== 'PLACEHOLDER_VALUE'
    ? container.containerNumber
    : containerIndex + 1;

  return (
    <div>
      <SectionHeader
        title={`Container ${displayContainerNumber}`}
        actions={
          isEditing ? (
            <>
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                onClick={handleSave}
                loading={saving}
                size="small"
              >
                Save
              </Button>
              <Button 
                icon={<CloseOutlined />} 
                onClick={handleCancel}
                disabled={saving}
                size="small"
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button 
              icon={<EditOutlined />} 
              onClick={handleEdit}
              size="small"
            >
              Edit
            </Button>
          )
        }
      />

      <SectionDivider />

      {container.isShared && (
        <SharedContainerIndicator
          isShared={container.isShared}
          myNetWeight={container.myNetWeight}
          totalNetWeight={container.totalNetWeight}
          sharedWith={container.sharedWith}
          validationError={container.validationError}
          containerNumber={displayContainerNumber.toString()}
        />
      )}

      {isEditing ? (
        <Form form={form} layout="vertical">
          <GridLayout columns="repeat(auto-fit, minmax(260px, 1fr))" style={{ marginBottom: spacing.xl }}>
            <Form.Item label="Container #" name="containerNumber">
              <Input placeholder="e.g. ABCU1234567" />
            </Form.Item>
            <Form.Item label="Seal #" name="sealNumber">
              <Input placeholder="e.g. S123456" />
            </Form.Item>
            <Form.Item label="Gross Weight (lbs)" name="grossWeight">
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Enter gross weight"
                min={0}
              />
            </Form.Item>
            <Form.Item label="Net Weight (lbs)" name="netWeight">
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Enter net weight"
                min={0}
              />
            </Form.Item>
            <Form.Item label="Equipment" name="equipmentType">
              <Select placeholder="Select equipment type" allowClear>
                <Option value="TWENTY_FT">20ft</Option>
                <Option value="FORTY_FT">40ft</Option>
                <Option value="FORTY_HC">40' HC</Option>
                <Option value="TWENTY_REEFER">20' Reefer</Option>
                <Option value="FORTY_REEFER">40' Reefer</Option>
                <Option value="AIR_FREIGHT">Air Freight</Option>
                <Option value="LCL">LCL</Option>
                <Option value="TRUCK">Truck</Option>
              </Select>
            </Form.Item>
          </GridLayout>
        </Form>
      ) : (
        <GridLayout columns="repeat(auto-fit, minmax(260px, 1fr))" style={{ marginBottom: spacing.xl }}>
          <ContractDataRow label="Container #" value={container.details?.containerNumber || 'N/A'} />
          <ContractDataRow label="Seal #" value={container.details?.sealNumber || 'N/A'} />
          <WeightDataRow
            label="Gross Weight"
            weight={{
              lbs: container.details?.grossWeight
                ? `${Number(container.details.grossWeight).toLocaleString()} lbs`
                : 'N/A',
              kg: container.details?.grossWeight
                ? `${(Number(container.details.grossWeight) * 0.45359237).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`
                : 'N/A'
            }}
          />
          <WeightDataRow
            label="Net Weight"
            weight={{
              lbs: container.details?.netWeight
                ? `${Number(container.details.netWeight).toLocaleString()} lbs`
                : 'N/A',
              kg: container.details?.netWeight
                ? `${(Number(container.details.netWeight) * 0.45359237).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`
                : 'N/A'
            }}
          />
          <ContractDataRow label="Equipment" value={container.details?.equipment || 'N/A'} />
        </GridLayout>
      )}
    </div>
  );
};

export default ContainerCard;
